import AppAlert from '@nxsys/components/AppAlert';
import AppBackButton from '@nxsys/components/AppBackButton';
import AppButton from '@nxsys/components/AppButton';
import AppsContainer from '@nxsys/components/AppsContainer';
import type { SidebarTabItem } from '@nxsys/components/AppSidebarTabLayout';
import AppSidebarTabLayout from '@nxsys/components/AppSidebarTabLayout';
import AppTagStatus from '@nxsys/components/AppTagStatus';
import { UMBRELLA_PAYE, UMBRELLA_PAYE_HYBRID } from '@nxsys/constants/AppConst';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import {
  useCisVerify,
  useResendLoginIdMultiple,
  useUpdateOnHoldEmployee,
} from '@nxsys/hooks/api';
import {
  useIsAllowedPersonalDetailsUpdate,
  useIsAllowedContactUpdate,
  useIsAllowedAddressUpdate,
  useIsAllowedBankDetailsUpdate,
  useIsAllowedHolidayPayShow,
} from '@nxsys/hooks/useEmployeePermission';
import { useGoBackByQuery } from '@nxsys/hooks/useGoBackByQuery';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppDispatch, AppState } from '@nxsys/store';
import { employeeActions } from '@nxsys/store/slices/employee/reducer/employeeSlice';
import { roles } from '@nxsys/store/constants';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { formatStatus } from '@nxsys/utils';
import { showMessage } from '@nxsys/utils/message';
import { Space, Tooltip, Typography } from 'antd';
import { includes } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import HoldEmployeeModal, {
  HoldEmployeeValues,
} from '../components/HoldEmployeeModal';
import Address from './Address';
import Assignment from './Assignment';
import AttachmentOrder from './AttachmentOrder';
import BackOffice from './BackOffice';
import BankInformation from './BankInformation';
import { canUseBenefitInKindTab } from '@nxsys/constants/AppConstants';
import BIKListing from './BIKListing';
import Document from './Document';
import EmailLog from './EmailLog';
import EmploymentTab from './Employment';
import EmploymentHistoryTab from './EmploymentHistoryTab';
import GeneralInformation from './GeneralInformation';
import MessageHistory from './MessageHistory';
import NoteListing from './NoteListing';
import PaymentDeduction from './PaymentDeduction';
import Payslip from './Payslip';
import Pension from './Pension';
import PensionIreland from './PensionIreland';
import SettingsExpense from './SettingsExpense';
import SettingsGeneral from './SettingsGeneral';
import SettingsHolidayPay from './SettingsHolidayPay';
import SettingsOthers from './SettingsOthers';
import Timesheet from './Timesheet';
import RPNUpdateTab from './RPNUpdateTab';
import YTDHistoryTab from './YTDHistoryTab';
import ActivityLog from './ActivityLog';
import { EmployeeTabKey } from './employeeTabKey';
import { DrawerSaveContext, type DrawerSaveApi } from './useDrawerSave';

const SETTINGS_GROUP_KEY = 'SETTINGS';
const DOCUMENTS_GROUP_KEY = 'DOCUMENTS';
const PAYMENTS_DEDUCTIONS_GROUP_KEY = 'PAYMENTS_DEDUCTIONS';

type TabItem = SidebarTabItem;

export type EmployeeDetailProps = {
  /** Render inside a drawer/modal: drive tabs via local state instead of the
   *  URL, hide the back-arrow, and take the worker id from a prop. */
  embedded?: boolean;
  employeeId?: string;
  initialTab?: EmployeeTabKey;
  /** antd Form field name to scroll to + focus once data loads. */
  focusField?: string;
};

const EmployeeDetail = ({
  embedded = false,
  employeeId: employeeIdProp,
  initialTab,
  focusField,
}: EmployeeDetailProps = {}) => {
  const { messages } = useTypedIntl();
  const params = useParams();
  const navigate = useNavigateWithQuery();
  const [searchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const { goBack } = useGoBackByQuery();

  const { user, payrollCountry, isEmployee, isBureau, isAgency } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;
  const canEditPersonalDetails = useIsAllowedPersonalDetailsUpdate();
  const canEditContact = useIsAllowedContactUpdate();
  const canEditAddress = useIsAllowedAddressUpdate();
  const canEditBankDetails = useIsAllowedBankDetailsUpdate();
  const canShowHolidayPay = useIsAllowedHolidayPayShow();
  const employeeId = isEmployee
    ? user?.subcontractorId?.id
    : (employeeIdProp ?? params?.employeeId ?? null);

  const dispatch = useDispatch<AppDispatch>();
  const isFetchingEmployee = useSelector(
    (state: AppState) => state.employeeStore.employeeDetail.loading,
  );
  const employeeData =
    useSelector((state: AppState) => state.employeeStore.employeeDetail.data) ??
    undefined;
  const fetchEmployee = useCallback(
    (id: string) => dispatch(employeeActions.getEmployeeById(id)),
    [dispatch],
  );

  // NX-4682: the active quick-edit tab registers its Save here (via
  // DrawerSaveContext) so the embedded drawer shows one always-visible Save in
  // the header.
  const [activeSaveApi, setActiveSaveApi] = useState<DrawerSaveApi | null>(
    null,
  );
  const registerSave = useCallback(
    (api: DrawerSaveApi | null) => setActiveSaveApi(api),
    [],
  );

  // Pension error state
  const errPension = useMemo(() => {
    const pension = employeeData?.pensionSetting;
    return {
      smartError:
        employeeData?.errorSubmittingSmartPension ||
        employeeData?.errorUpdatingSmartPension ||
        employeeData?.errorLeaveSmartPension,
      jarvisError: pension?.jarvisPensionError ?? null,
    };
  }, [employeeData]);

  const basePath = isEmployee
    ? `/general`
    : `/employees-subcontractors/${employeeData?.id}`;

  useEffect(() => {
    if (employeeId) fetchEmployee(employeeId);
    return () => {
      dispatch(employeeActions.clearEmployeeDetail());
      dispatch(employeeActions.clearEmployeeSetting());
    };
  }, [employeeId]);

  const isCisWorker =
    employeeData?.setting?.paymentModel === PAYMENT_MODEL_ENUM.CIS ||
    employeeData?.setting?.paymentModel === PAYMENT_MODEL_ENUM.NON_CIS;

  const settingsGroup: TabItem = {
    label: messages['common.settings'] ?? 'Settings',
    key: SETTINGS_GROUP_KEY,
    children: [
      {
        label: messages['common.general'] ?? 'General',
        key: EmployeeTabKey.SETTINGS_GENERAL,
      },
      ...(!isCisWorker
        ? [
            {
              label: messages['common.holidayPay'],
              key: EmployeeTabKey.SETTINGS_HOLIDAY_PAY,
            },
          ]
        : []),
      ...(includes(
        [PAYMENT_MODEL_ENUM.UMBRELLA, PAYMENT_MODEL_ENUM.HYBRID],
        employeeData?.setting?.paymentModel,
      ) &&
      ((employeeData as any)?.submittedExpenseCount > 0 ||
        employeeData?.setting?.expenseClaimRule === 'ALLOW')
        ? [
            {
              label: messages['common.expense'],
              key: EmployeeTabKey.SETTINGS_EXPENSE,
            },
          ]
        : []),
      {
        label: messages['common.other'] ?? 'Others',
        key: EmployeeTabKey.SETTINGS_OTHERS,
      },
    ],
  };

  const tabs = (): TabItem[] => {
    if (isEmployee)
      return [
        {
          label: messages['common.personalDetail'],
          key: EmployeeTabKey.GENERAL_INFORMATION,
        },
        {
          label: messages['common.address'],
          key: EmployeeTabKey.ADDRESS,
        },
        {
          label: messages['common.bankAccount'],
          key: EmployeeTabKey.BANK_INFORMATION,
        },
        ...(!isIreland && !isCisWorker
          ? [
              {
                label: messages['common.attachmentOrder'],
                key: EmployeeTabKey.ATTACHMENT_ORDER,
              },
            ]
          : []),
        ...(!isCisWorker
          ? [
              {
                label: messages['common.pension'],
                key: EmployeeTabKey.PENSION,
              },
            ]
          : []),
        {
          ...settingsGroup,
          label: messages['common.payrollSettings'],
          children: settingsGroup.children?.filter(
            (child) =>
              (child.key !== EmployeeTabKey.SETTINGS_HOLIDAY_PAY ||
                canShowHolidayPay) &&
              (child.key !== EmployeeTabKey.SETTINGS_OTHERS || !isIreland),
          ),
        },
        ...(isBureau && !isIreland
          ? [
              {
                label: messages['common.paymentsAndDeductions'],
                key: PAYMENTS_DEDUCTIONS_GROUP_KEY,
                children: [
                  {
                    label: messages['common.payment'],
                    key: EmployeeTabKey.PAYMENTS,
                  },
                  {
                    label: messages['common.deduction'],
                    key: EmployeeTabKey.DEDUCTIONS,
                  },
                ],
              },
            ]
          : []),
        ...(isIreland
          ? [
              {
                label: messages['common.rpnUpdate'] ?? 'RPN Update',
                key: EmployeeTabKey.RPN_UPDATE,
              },
            ]
          : []),
        {
          label: messages['common.employmentHistory'],
          key: EmployeeTabKey.EMPLOYMENT_HISTORY,
        },
      ];

    if (isAgency) {
      return [
        {
          label: messages['common.personalDetail'] ?? 'Personal Detail',
          key: EmployeeTabKey.GENERAL_INFORMATION,
        },
        {
          label: messages['common.bankDetails'] ?? 'Bank Details',
          key: EmployeeTabKey.BANK_INFORMATION,
        },
        settingsGroup,
        {
          label: messages['common.assignment'],
          key: EmployeeTabKey.ASSIGNMENT,
        },
        {
          label: messages['common.timesheet'],
          key: EmployeeTabKey.TIMESHEET,
        },
        {
          label: isCisWorker
            ? messages['common.selfBill']
            : messages['common.payslip'],
          key: EmployeeTabKey.PAYSLIP,
        },
      ];
    }

    return [
      {
        label: messages['common.personalDetail'] ?? 'Personal Detail',
        key: EmployeeTabKey.GENERAL_INFORMATION,
      },
      {
        label: messages['common.address'],
        key: EmployeeTabKey.ADDRESS,
      },
      {
        label: messages['common.bankDetails'] ?? 'Bank Details',
        key: EmployeeTabKey.BANK_INFORMATION,
      },
      {
        label: messages['common.employment'] ?? 'Employment',
        key: EmployeeTabKey.EMPLOYMENT,
      },
      ...(includes(UMBRELLA_PAYE, employeeData?.setting?.paymentModel) &&
      !employeeData?.pensionSetting?.outsideUKWorker
        ? [
            {
              label: messages['common.pension'],
              key: EmployeeTabKey.PENSION,
            },
          ]
        : []),
      ...(includes(UMBRELLA_PAYE_HYBRID, employeeData?.setting?.paymentModel) &&
      !isIreland
        ? [
            {
              label: messages['common.attachmentOrder'],
              key: EmployeeTabKey.ATTACHMENT_ORDER,
            },
          ]
        : []),
      settingsGroup,
      {
        label: messages['common.document'] ?? 'Documents',
        key: DOCUMENTS_GROUP_KEY,
        children: [
          {
            label: messages['common.onboarding'] ?? 'Onboarding',
            key: EmployeeTabKey.DOCUMENTS_ONBOARDING,
          },
          {
            label: messages['common.other'] ?? 'Other',
            key: EmployeeTabKey.DOCUMENTS_OTHER,
          },
        ],
      },
      {
        label: messages['common.paymentsAndDeductions'],
        key: PAYMENTS_DEDUCTIONS_GROUP_KEY,
        children: [
          {
            label: messages['common.payment'] ?? 'Payments',
            key: EmployeeTabKey.PAYMENTS,
          },
          {
            label: messages['common.deduction'] ?? 'Deductions',
            key: EmployeeTabKey.DEDUCTIONS,
          },
        ],
      },
      ...(isIreland
        ? [
            {
              label: messages['common.rpnUpdate'] ?? 'RPN Update',
              key: EmployeeTabKey.RPN_UPDATE,
            },
          ]
        : []),
      ...(canUseBenefitInKindTab(
        isIreland,
        employeeData?.setting?.paymentModel,
      )
        ? [
            {
              label: messages['common.benefitInKind'],
              key: EmployeeTabKey.BIK,
            },
          ]
        : []),
      ...(!isBureau &&
      includes(
        [PAYMENT_MODEL_ENUM.UMBRELLA, PAYMENT_MODEL_ENUM.HYBRID],
        employeeData?.setting?.paymentModel,
      )
        ? [
            {
              label: messages['common.assignment'],
              key: EmployeeTabKey.ASSIGNMENT,
            },
          ]
        : []),
      {
        label: messages['common.timesheet'],
        key: EmployeeTabKey.TIMESHEET,
      },
      ...(includes(UMBRELLA_PAYE, employeeData?.setting?.paymentModel) ||
      isCisWorker
        ? [
            {
              label: isCisWorker
                ? messages['common.selfBill']
                : messages['common.payslip'],
              key: EmployeeTabKey.PAYSLIP,
            },
          ]
        : []),
      {
        label: messages['common.messageHistory'],
        key: EmployeeTabKey.MESSAGE_HISTORY,
      },
      {
        label: messages['common.notes'],
        key: EmployeeTabKey.NOTE_LISTING,
      },
      {
        label: messages['common.backOffice'],
        key: EmployeeTabKey.BACK_OFFICE,
      },
      {
        label: messages['common.activityLog'],
        key: EmployeeTabKey.ACTIVITY_LOG,
      },
      ...(!isIreland
        ? [
            {
              label: messages['common.ytdHistory'],
              key: EmployeeTabKey.YTD_HISTORY,
            },
          ]
        : []),
      {
        label: messages['common.employmentHistory'],
        key: EmployeeTabKey.EMPLOYMENT_HISTORY,
      },
      {
        label: messages['common.emailLog'],
        key: EmployeeTabKey.EMAIL_LOG,
      },
    ];
  };

  const [activeTab, setActiveTab] = useState<EmployeeTabKey>(
    initialTab ?? EmployeeTabKey.GENERAL_INFORMATION,
  );
  useEffect(() => {
    if (embedded) return;
    if (tabParam) {
      setActiveTab(tabParam as EmployeeTabKey);
    }
  }, [tabParam, embedded]);

  const renderTabContent = useCallback(
    (key: EmployeeTabKey) => {
      switch (key) {
        case EmployeeTabKey.GENERAL_INFORMATION:
          return (
            <GeneralInformation
              data={employeeData}
              focusField={focusField}
              readOnly={isAgency || (isEmployee && !canEditPersonalDetails)}
              readOnlyContact={isAgency || (isEmployee && !canEditContact)}
              onRefetch={() => {
                if (employeeId) fetchEmployee(employeeId);
              }}
              onResendLogin={
                !(isEmployee || isAgency) ? handleResendLoginId : undefined
              }
              isResendLoginLoading={isResendingLogin}
            />
          );
        case EmployeeTabKey.ADDRESS:
          return (
            <Address
              data={employeeData}
              focusField={focusField}
              readOnly={isAgency || (isEmployee && !canEditAddress)}
              onRefetch={() => {
                if (employeeId) fetchEmployee(employeeId);
              }}
            />
          );
        case EmployeeTabKey.BANK_INFORMATION:
          return (
            <BankInformation
              data={employeeData}
              readOnly={isAgency || (isEmployee && !canEditBankDetails)}
              employeeId={employeeId}
            />
          );
        case EmployeeTabKey.EMPLOYMENT:
          return (
            <EmploymentTab
              data={employeeData}
              onRefetch={() => {
                if (employeeId) fetchEmployee(employeeId);
              }}
            />
          );
        case EmployeeTabKey.SETTINGS_GENERAL:
          return (
            <SettingsGeneral
              readOnly={isEmployee || isAgency}
              employeeId={employeeId ?? undefined}
              focusField={focusField}
            />
          );
        case EmployeeTabKey.SETTINGS_HOLIDAY_PAY:
          return <SettingsHolidayPay readOnly={isEmployee || isAgency} />;
        case EmployeeTabKey.SETTINGS_EXPENSE:
          return (
            <SettingsExpense
              data={employeeData}
              readOnly={isEmployee || isAgency}
            />
          );
        case EmployeeTabKey.SETTINGS_OTHERS:
          return (
            <SettingsOthers
              data={employeeData}
              readOnly={isEmployee || isAgency}
            />
          );
        case EmployeeTabKey.DOCUMENTS_ONBOARDING:
          return <Document employeeId={employeeId!} defaultTab="ONBOARDING" />;
        case EmployeeTabKey.DOCUMENTS_OTHER:
          return <Document employeeId={employeeId!} defaultTab="OTHER" />;
        case EmployeeTabKey.NOTE_LISTING:
          return <NoteListing employeeId={employeeId!} />;
        case EmployeeTabKey.PAYMENTS:
          return (
            <PaymentDeduction
              employeeId={employeeId!}
              defaultTab="PAYMENT"
              paymentModel={employeeData?.setting?.paymentModel ?? undefined}
            />
          );
        case EmployeeTabKey.DEDUCTIONS:
          return (
            <PaymentDeduction
              employeeId={employeeId!}
              defaultTab="DEDUCTION"
              paymentModel={employeeData?.setting?.paymentModel ?? undefined}
            />
          );
        case EmployeeTabKey.ASSIGNMENT:
          return (
            <Assignment employeeData={employeeData!} readOnly={isAgency} />
          );
        case EmployeeTabKey.TIMESHEET:
          return <Timesheet employeeId={employeeId!} readOnly={isAgency} />;
        case EmployeeTabKey.PAYSLIP:
          return <Payslip employeeId={employeeId!} />;
        case EmployeeTabKey.BACK_OFFICE:
          return <BackOffice employeeId={employeeId!} />;
        case EmployeeTabKey.ATTACHMENT_ORDER:
          return (
            <AttachmentOrder employeeId={employeeId!} readOnly={isEmployee} />
          );
        case EmployeeTabKey.PENSION:
          return isIreland ? (
            <PensionIreland
              employeeData={employeeData!}
              readOnly={isEmployee}
            />
          ) : (
            <Pension employeeData={employeeData!} readOnly={isEmployee} />
          );
        case EmployeeTabKey.BIK:
          return <BIKListing employeeId={employeeId!} />;
        case EmployeeTabKey.MESSAGE_HISTORY:
          return <MessageHistory employeeData={employeeData} />;
        case EmployeeTabKey.ACTIVITY_LOG:
          return <ActivityLog employeeId={employeeId!} />;
        case EmployeeTabKey.YTD_HISTORY:
          return <YTDHistoryTab employeeId={employeeId!} />;
        case EmployeeTabKey.EMPLOYMENT_HISTORY:
          return <EmploymentHistoryTab employeeId={employeeId!} />;
        case EmployeeTabKey.EMAIL_LOG:
          return <EmailLog employeeId={employeeId!} />;
        case EmployeeTabKey.RPN_UPDATE:
          return (
            <RPNUpdateTab employeeId={employeeId!} readOnly={isEmployee} />
          );
        default:
          return <></>;
      }
    },
    [
      employeeData,
      isEmployee,
      canEditPersonalDetails,
      canEditAddress,
      canEditBankDetails,
      focusField,
    ],
  );

  const handleGoBack = () =>
    goBack({
      fromPathQueryKey: 'from',
      fallbackPathDefault: '/employees-subcontractors',
      excludeParams: ['tab'],
    });

  const renderTitle = useCallback(
    () => (
      <Space>
        {!isEmployee && !embedded && (
          <AppBackButton onClick={handleGoBack} />
        )}
        <Space direction="vertical" size={6}>
          <div className="flex items-center gap-x-2">
            <Typography.Title level={4} className="mb-0">
              {employeeData?.fullName ?? ''}
            </Typography.Title>
            <AppTagStatus className={formatStatus(employeeData?.status ?? '')}>
              {formatStatus(employeeData?.status ?? '')}
            </AppTagStatus>
          </div>
          <div className="flex items-center gap-x-2">
            {/* NX-5705: append current employment id so the header shows the full payroll ID reported to HMRC */}
            <Typography.Text className="font-sm text-gray-500 border-solid border-gray-200 border-r pr-2">{`ID: ${employeeData?.id ?? ''}${!isIreland && employeeData?.currentEmploymentId ? `-${employeeData.currentEmploymentId}` : ''}`}</Typography.Text>
            <Typography.Text className="font-sm text-gray-500">{`${messages['common.updatedOn']}: ${moment(employeeData?.updatedAt).format('DD/MM/YYYY')}`}</Typography.Text>
          </div>
        </Space>
      </Space>
    ),
    [employeeData, isIreland],
  );

  const renderRightContent = useCallback(() => {
    if (isEmployee || isAgency) return null;

    return (
      <div className="flex items-center gap-x-4">
        {embedded && activeSaveApi && (
          <AppButton
            type="primary"
            name={messages['common.save']}
            leftIcon={
              <Icons.IconCheckCircle color={defaultTheme.theme.palette.white} />
            }
            onClick={activeSaveApi.save}
            loading={activeSaveApi.saving}
          />
        )}
        {isCisWorker && user?.role === roles.admin && (
          <Tooltip title={messages['common.verify']}>
            <span className="cursor-pointer" onClick={handleCisVerify}>
              <Icons.IconCheckCircle />
            </span>
          </Tooltip>
        )}
        <Tooltip title={messages['common.resendLoginId']}>
          <span
            className="cursor-pointer"
            onClick={() => handleResendLoginId()}
          >
            <Icons.IconSend />
          </span>
        </Tooltip>
        <Tooltip
          title={
            employeeData?.isOnHold
              ? messages['common.unHold']
              : messages['common.hold']
          }
        >
          <span className="cursor-pointer" onClick={() => handleUpdateHold()}>
            <Icons.IconShield
              color={employeeData?.isOnHold ? '#ef4444' : '#22c55e'}
            />
          </span>
        </Tooltip>
      </div>
    );
  }, [
    messages,
    employeeData,
    isAgency,
    isEmployee,
    isCisWorker,
    user,
    embedded,
    activeSaveApi,
  ]);

  const [isResendingLogin, setIsResendingLogin] = useState(false);
  useNotificationSocket({
    eventName: `resend-mail-login-id-${user?.id}`,
    onSuccess: (data) => {
      setIsResendingLogin(false);
      if (data?.success) {
        showMessage({
          type: 'success',
          content: messages['common.resendLoginIdSuccess'],
        });
        if (employeeId) fetchEmployee(employeeId);
      }
    },
  });
  const { run: resendLoginIdMultiple } = useResendLoginIdMultiple();
  const handleResendLoginId = () => {
    if (!employeeData?.id) return;
    setIsResendingLogin(true);
    resendLoginIdMultiple({
      ids: [employeeData.id],
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['common.resendLoginIdPending'],
      });
    });
  };

  const [openHoldModal, setOpenHoldModal] = useState(false);
  const { run: updateOnHoldEmployee, loading: isUpdateOnHoldEmployeeLoading } =
    useUpdateOnHoldEmployee();
  const handleOnHoldEmployee = async (values: HoldEmployeeValues) => {
    if (!employeeData?.id) return;
    const res = await updateOnHoldEmployee(employeeData?.id, {
      isOnHold: true,
      holdReason: values.reason,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.employeeOnHoldSuccess'],
      });
      if (employeeId) fetchEmployee(employeeId);
    }
  };
  const handleUnHoldEmployee = async () => {
    if (!employeeData?.id) return;
    const res = await updateOnHoldEmployee(employeeData?.id, {
      isOnHold: false,
      holdReason: null,
    });
    if (res.success) {
      showMessage({
        type: 'success',
        content: messages['common.employeeUnHoldSuccess'],
      });
      if (employeeId) fetchEmployee(employeeId);
    }
  };
  const handleUpdateHold = () => {
    if (employeeData?.isOnHold) {
      handleUnHoldEmployee();
    } else {
      setOpenHoldModal(true);
    }
  };

  const { run: cisVerify, loading: isCisVerifyLoading } = useCisVerify();
  const handleCisVerify = async () => {
    if (!employeeData?.id || !employeeData?.companyPayment?.id) return;
    const res = await cisVerify({
      companyId: employeeData.companyPayment.id,
      subcontractorsRef: [employeeData.id],
      isTesting: import.meta.env.VITE_IS_TESTING_API === 'true',
    });
    if (res?.success) {
      fetchEmployee(employeeData.id);
      // NX-5657: the Tax Settings form populates from the employeeSetting
      // store, not employeeDetail — without this the verified PVN/CIS
      // status/date never appear until a full page reload.
      dispatch(employeeActions.getPayrollSettingByEmployeeId(employeeData.id));
    }
  };

  return (
    <div className="AgencyDetail">
      <DrawerSaveContext.Provider value={embedded ? registerSave : null}>
        <AppsContainer
          fullView
          type="bottom"
          title={renderTitle()}
          rightContent={renderRightContent()}
          loading={
            isFetchingEmployee ||
            isResendingLogin ||
            isUpdateOnHoldEmployeeLoading ||
            isCisVerifyLoading
          }
        >
          {(errPension.smartError || errPension.jarvisError) && (
            <div className="mt-4 mb-4 px-4">
              {errPension.smartError && (
                <AppAlert
                  type="error"
                  variant="error"
                  message={`Smart Pension Error: ${errPension.smartError}`}
                />
              )}
              {errPension.jarvisError && (
                <AppAlert
                  type="warning"
                  variant="warning"
                  message={errPension.jarvisError}
                />
              )}
            </div>
          )}
          <AppSidebarTabLayout
            tabs={tabs()}
            activeTab={activeTab}
            onTabChange={(key) => {
              if (embedded) {
                setActiveTab(key as EmployeeTabKey);
                return;
              }
              navigate(basePath, { query: { tab: key } });
            }}
            contentClassName={
              [
                EmployeeTabKey.ATTACHMENT_ORDER,
                EmployeeTabKey.DOCUMENTS_ONBOARDING,
                EmployeeTabKey.DOCUMENTS_OTHER,
                EmployeeTabKey.NOTE_LISTING,
                EmployeeTabKey.PAYMENTS,
                EmployeeTabKey.DEDUCTIONS,
                EmployeeTabKey.ASSIGNMENT,
                EmployeeTabKey.TIMESHEET,
                EmployeeTabKey.PAYSLIP,
                EmployeeTabKey.BACK_OFFICE,
                EmployeeTabKey.EXPENSE,
                EmployeeTabKey.BIK,
                EmployeeTabKey.MESSAGE_HISTORY,
                EmployeeTabKey.ACTIVITY_LOG,
                EmployeeTabKey.YTD_HISTORY,
                EmployeeTabKey.EMPLOYMENT_HISTORY,
                EmployeeTabKey.EMAIL_LOG,
                EmployeeTabKey.PENSION,
                EmployeeTabKey.SETTINGS_EXPENSE,
                EmployeeTabKey.RPN_UPDATE,
              ].includes(activeTab)
                ? 'px-6'
                : 'max-w-[472px]'
            }
          >
            {employeeData && renderTabContent(activeTab)}
          </AppSidebarTabLayout>
        </AppsContainer>
        <HoldEmployeeModal
          isOpen={openHoldModal}
          toggle={() => setOpenHoldModal(!openHoldModal)}
          onSubmit={handleOnHoldEmployee}
        />
      </DrawerSaveContext.Provider>
    </div>
  );
};

export default EmployeeDetail;
